import React from "react";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { DatePicker } from "@/components/datePicker";
import type { RowState } from "@/components/advanceSearch/types";
import { fallbackShortDateFormat } from "../../constants";
import { formatISODate } from "@/utils";

interface Props {
  row: RowState;
  control: any;
  onClear: (which: "value" | "value2") => void;
  shortDateFormat?: string;
}

export const ConditionDateInput: React.FC<Props> = ({
  row,
  control,
  onClear,
  shortDateFormat = fallbackShortDateFormat
}) => {
  const isBetween = row.operator === "between";

  const buildAriaLabel = (isEnd?: boolean) => {
    if (isEnd) return "Select end date";
    if (isBetween) return "Select start date";
    return "Select date";
  };

  const buildClearLabel = (isEnd?: boolean) => {
    if (isEnd) return "Clear end date";
    if (isBetween) return "Clear start date";
    return "Clear date";
  };

  const renderPicker = (fieldName: string, which: "value" | "value2", options?: { isEnd?: boolean }) => (
    <FormField
      control={control}
      name={fieldName}
      rules={{ required: "This field is required." }}
      render={({ field, fieldState }) => {
        const handleValueChange = (next?: string) => {
          field.onChange(next ?? undefined);
          if (!next) {
            onClear(which);
          }
        };

        return (
          <FormItem className="relative w-full min-w-0">
            <FormControl>
              <DatePicker
                {...field}
                value={field.value || undefined}
                onValueChange={handleValueChange}
                placeholder={shortDateFormat}
                ariaLabel={buildAriaLabel(options?.isEnd)}
                clearAriaLabel={buildClearLabel(options?.isEnd)}
                invalid={Boolean(fieldState.error)}
                displayFormatter={(d) => formatISODate(d, shortDateFormat)}
                wrapperClassName="min-w-0"
              />
            </FormControl>
            <FormMessage className={"absolute left-0 top-full mt-1 text-xs text-red-600"} />
          </FormItem>
        );
      }}
    />
  );

  if (isBetween) {
    return (
      <div className="grid items-center gap-3 md:grid-cols-[1fr_auto_1fr] max-w-full min-w-0">
        <div className="min-w-0">{renderPicker(`value_${row.id}`, "value")}</div>
        <span className="text-center font-bold text-inherit whitespace-nowrap">To</span>
        <div className="min-w-0">{renderPicker(`value2_${row.id}`, "value2", { isEnd: true })}</div>
      </div>
    );
  }

  return renderPicker(`value_${row.id}`, "value");
};
